import CloseIcon from '@mui/icons-material/Close';
import {
	Stack,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	Typography,
	Container,
	Box,
} from '@mui/material';

import React, { useEffect, useState } from 'react';

import { useDispatch } from 'react-redux';

import { ReactComponent as ExpandMoreIcon } from 'common/assets/images/downchevron.svg';
import ButtonComp from 'common/components/Button/ButtonComp';
import ModalEle from 'common/components/Modal/ModalEle';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import { APP_GENERAL_ERROR_MSG } from 'common/constants/TextContent';
import { setToastMessage } from 'common/store/reducer/commonReducer';

import { ITestSelectControlData } from 'features/TestMonitoring/api/type-def';

import { getExistingTestScripts } from './api';

import styles from './ExistingTestScripts.module.scss';

interface ExistingTestScriptsProps {
	openModal: boolean;
	onClose: () => void;
	controlData: ITestSelectControlData;
	selectedOption: string;
	onSelectTestScript: (data: any) => void;
}

const { OK, BAD_REQUEST } = HTTP_STATUS_CODES;

const ExistingTestScripts: React.FC<ExistingTestScriptsProps> = ({
	openModal,
	onClose,
	controlData,
	selectedOption,
	onSelectTestScript,
}) => {
	const [apiData, setApiData] = useState<string[]>([]);
	const [expandedAccordion, setExpandedAccordion] = useState<boolean>(false);
	const dispatch = useDispatch();

	const [isSelectTestScriptDisabled, setIsSelectTestScriptDisabled] =
		useState(true);
	const selectedControl = controlData.controls.find(
		(control) => control.controlId === selectedOption,
	);

	useEffect(() => {
		if (openModal) {
			setExpandedAccordion(false);
		}
	}, [openModal]);

	const fetchExistingTestScript = async () => {
		const response = await getExistingTestScripts(selectedOption);

		if (response?.statusCode === OK) {
			const fullStringValue = response.responseBody.testScript;
			setIsSelectTestScriptDisabled(false);
			let i = 1;
			const result = [];
			while (i <= fullStringValue.length - 1) {
				const newStr = fullStringValue.indexOf(`${i}.`);
				const newStr2 = fullStringValue.indexOf(`${i + 1}.`);

				if (newStr === -1) {
					break;
				} else {
					if (newStr2 !== -1) {
						const strn = fullStringValue.substring(newStr + 2, newStr2);
						result.push(strn);
					} else {
						const strn = fullStringValue.substring(
							newStr + 2,
							fullStringValue.length - 1,
						);
						result.push(strn);
					}
					i += 1;
				}
			}

			setApiData(result);
		} else if (response?.statusCode === BAD_REQUEST) {
			const { errorBody } = response;
			const errorMsg = errorBody?.errorMessage as string;
			dispatch(
				setToastMessage({
					message: errorMsg,
					severity: 'error',
					isToastOpen: true,
				}),
			);
		} else {
			dispatch(
				setToastMessage({
					message: APP_GENERAL_ERROR_MSG,
					severity: 'error',
					isToastOpen: true,
				}),
			);
		}
	};

	const handleSelectTestScript = () => {
		const selectedScript = apiData;
		if (expandedAccordion) {
			if (selectedScript !== null) {
				callOnTestScript();
			}
		} else {
			callOnTestScript();
		}
	};

	const callOnTestScript = () => {
		onSelectTestScript(apiData);
		onClose();
	};

	useEffect(() => {
		if (selectedOption) {
			fetchExistingTestScript();
		}
	}, [selectedOption]);

	return (
		<ModalEle open handleClose={onClose}>
			<Container className={styles['add-container']}>
				<div>
					<Stack direction="row" justifyContent="space-between">
						<Typography className={styles['header-text']} data-testid="header">
							{`Existing ${selectedControl?.controlName} Test Scripts`}
						</Typography>
						<CloseIcon
							fontSize="medium"
							onClick={onClose}
							className={styles['add-closemodal']}
							data-testid="close-icon"
						/>
					</Stack>
					<Box className={styles['border-style']}>
						<Accordion
							expanded={expandedAccordion}
							className={`${expandedAccordion && styles['selected-accordion']}`}
							onChange={() => {
								setExpandedAccordion(!expandedAccordion);
							}}
							data-testid="accordion-summary"
						>
							<AccordionSummary expandIcon={<ExpandMoreIcon />}>
								<Typography>
									{selectedControl?.controlName} Test script one
								</Typography>
							</AccordionSummary>
							<AccordionDetails>
								<div className={styles['p-style']}>
									{apiData?.length > 0 &&
										apiData.map((ele: any, index: number) => (
											<p key={ele}>{`${index + 1}. ${ele}`}</p>
										))}
								</div>
							</AccordionDetails>
						</Accordion>
					</Box>
				</div>

				<Stack
					direction="row"
					spacing={2}
					justifyContent="flex-end"
					display="flex"
					marginTop={2}
				>
					<ButtonComp btnType="secondary" clickHandler={onClose}>
						Cancel
					</ButtonComp>
					<ButtonComp
						btnType="primary"
						clickHandler={handleSelectTestScript}
						disabled={isSelectTestScriptDisabled}
					>
						Select test script
					</ButtonComp>
				</Stack>
			</Container>
		</ModalEle>
	);
};

export default ExistingTestScripts;
